import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AddFoodCardFailureReasonRequest,
  FoodCardFailureReasonTypeRecord,
  FoodCardFailureReasonTypeService,
} from '@libs/shared/api';
import { map, Observable } from 'rxjs';
import { allAllowInElements } from '@libs/shared/utils/const/shortcut';
import { injectHotkeyManagerService } from '@libs/shared/hotkey/hotkey-manager.service';

@Component({
  selector: 'app-add-food-card-failure-reason-dialog',
  template: `
    <h1 mat-dialog-title class="text-xl font-bold">
      분석에 실패한 이유는 무엇인가요?
    </h1>
    <div mat-dialog-content>
      <form [formGroup]="formGroup" (keydown.enter)="$event.preventDefault()">
        <div class="w-100 flex flex-col gap-2">
          <mat-radio-group formControlName="typeId">
            <div class="flex flex-col gap-2 p-2">
              @for (
                foodCardFailureReasonType of foodCardFailureReasonTypes$
                | async
                ; track
                foodCardFailureReasonType) {
                <mat-radio-button
                  [value]="foodCardFailureReasonType.id">
                  <div class="flex flex-row items-center gap-1">
                    <span>{{ foodCardFailureReasonType.internalTitle }}</span>
                    <mat-icon
                      class="icon-size-4"
                      [svgIcon]="'mat_outline:info'"
                    [matTooltip]="
                      foodCardFailureReasonType.internalDescription
                    " />
                  </div>
                </mat-radio-button>
              }
            </div>
          </mat-radio-group>
          @if (data.isAdmin) {
            <mat-form-field
              [floatLabel]="'always'"
              class="fuse-mat-no-header w-full"
              appearance="fill">
              <textarea
                matInput
                formControlName="remark"
              placeholder="추가 메시지 (Shift+Enter: 줄바꿈)"></textarea>
            </mat-form-field>
          }
        </div>
      </form>
    </div>
    <div mat-dialog-actions class="flex flex-row justify-end">
      <button mat-stroked-button (click)="onClose()">취소</button>
      <button
        mat-flat-button
        color="warn"
        [disabled]="formGroup.invalid"
        (click)="onSubmit()">
        제출하기
      </button>
    </div>
    `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFoodCardFailureReasonDialogComponent
  implements AfterViewInit, OnDestroy
{
  readonly fb = inject(FormBuilder);
  // prettier-ignore
  readonly foodCardFailureReasonTypeService = inject(FoodCardFailureReasonTypeService);
  readonly hotkeyManager = injectHotkeyManagerService();

  formGroup = this.initializeForm();
  foodCardFailureReasonTypes$: Observable<FoodCardFailureReasonTypeRecord[]>;

  constructor(
    // prettier-ignore
    public matDialogRef: MatDialogRef<AddFoodCardFailureReasonDialogComponent, AddFoodCardFailureReasonRequest>,
    @Inject(MAT_DIALOG_DATA)
    public data: { isAdmin: boolean }
  ) {
    this.foodCardFailureReasonTypes$ = this.foodCardFailureReasonTypeService
      .fetchFoodCardFailureReasonTypes(true)
      .pipe(
        map(failureReason => {
          if (!data.isAdmin) {
            return failureReason.filter(reason => reason.adminOnly === false);
          } else {
            return failureReason;
          }
        })
      );
  }

  ngAfterViewInit(): void {
    this.hotkeyManager.activateHotkeysForComponent(this.constructor.name, [
      {
        keys: 'meta.enter',
        description: '저장하기',
        allowIn: allAllowInElements,
        action: () => {
          this.onSubmit();
        },
      },
      {
        keys: 'escape',
        description: '닫기',
        allowIn: allAllowInElements,
        action: () => {
          this.onClose();
        },
      },
    ]);
  }

  ngOnDestroy(): void {
    this.hotkeyManager.deactivateHotkeys(this.constructor.name);
  }

  onSubmit() {
    if (this.formGroup.valid) {
      this.matDialogRef.close({
        remark: this.formGroup.value.remark,
        typeId: this.formGroup.value.typeId,
      });
    }
  }

  onClose() {
    this.matDialogRef.close();
  }

  private initializeForm(): AddFoodCardFailureReasonFormGroup {
    return this.fb.group({
      remark: this.fb.control(null),
      typeId: this.fb.control(null, [Validators.required]),
    });
  }
}

export type AddFoodCardFailureReasonFormGroup = FormGroup<{
    remark: FormControl<string | null>;
    typeId: FormControl<string | null>;
  }>
